/**
 * Drives the presign → direct-PUT-to-R2 → ready-to-send lifecycle for images
 * attached to a chat message. Each PendingUpload walks through:
 *   resizing → presigning → uploading → complete   (or → error)
 *
 * XMLHttpRequest (not fetch) for the PUT because fetch does not expose upload
 * progress events — without that we can't drive the thumbnail progress ring.
 */

import { useCallback, useState } from 'react';
import { apiFetch } from '../lib/api';
import { createThumbnail, resizeImageForUpload } from '../lib/imageResize';

export interface PendingUpload {
  id: string;
  file: File;
  thumbnailUrl: string;
  attachmentId?: string;
  publicUrl?: string;
  progress: number; // 0–100
  status: 'resizing' | 'presigning' | 'uploading' | 'complete' | 'error';
  error?: string;
}

interface PresignResponse {
  upload_url: string;
  attachment_id: string;
  public_url: string;
}

const ALLOWED_MIME = new Set([
  'image/jpeg',
  'image/png',
  'image/gif',
  'image/webp',
]);

export function useImageUpload() {
  const [uploads, setUploads] = useState<PendingUpload[]>([]);

  const updateUpload = useCallback(
    (id: string, updates: Partial<PendingUpload>) => {
      setUploads((prev) =>
        prev.map((u) => (u.id === id ? { ...u, ...updates } : u)),
      );
    },
    [],
  );

  const addImage = useCallback(
    async (file: File) => {
      if (!ALLOWED_MIME.has(file.type)) return;

      const localId = crypto.randomUUID();
      let thumbnailUrl = '';
      try {
        thumbnailUrl = await createThumbnail(file);
      } catch {
        // If thumbnail fails the image is probably unreadable — bail silently.
        return;
      }

      const pending: PendingUpload = {
        id: localId,
        file,
        thumbnailUrl,
        progress: 0,
        status: 'resizing',
      };
      setUploads((prev) => [...prev, pending]);

      try {
        const resized = await resizeImageForUpload(file);
        updateUpload(localId, { status: 'presigning', progress: 20 });

        const presign = await apiFetch<PresignResponse>('/api/upload/presign', {
          method: 'POST',
          body: {
            mime_type: resized.blob.type,
            size_bytes: resized.blob.size,
            width: resized.width,
            height: resized.height,
          },
        });

        updateUpload(localId, {
          attachmentId: presign.attachment_id,
          publicUrl: presign.public_url,
          status: 'uploading',
          progress: 40,
        });

        await uploadToR2(
          presign.upload_url,
          resized.blob,
          resized.blob.type,
          (fraction) => {
            updateUpload(localId, { progress: 40 + Math.round(fraction * 60) });
          },
        );

        updateUpload(localId, { status: 'complete', progress: 100 });
      } catch (err) {
        const message = err instanceof Error ? err.message : 'Upload failed';
        updateUpload(localId, { status: 'error', error: message });
      }
    },
    [updateUpload],
  );

  const removeUpload = useCallback((id: string) => {
    // TODO (Phase 5): if status === 'complete', also DELETE the R2 object and
    // the attachment row. Today this creates an orphan type 2 — covered by
    // the scheduled cleanup detailed in migrations/0004_attachments.sql.
    setUploads((prev) => prev.filter((u) => u.id !== id));
  }, []);

  const clearCompleted = useCallback(() => {
    setUploads((prev) => prev.filter((u) => u.status !== 'complete'));
  }, []);

  const clearAll = useCallback(() => {
    setUploads([]);
  }, []);

  const completedAttachmentIds = uploads
    .filter((u) => u.status === 'complete' && u.attachmentId)
    .map((u) => u.attachmentId!);

  const isUploading = uploads.some((u) =>
    u.status === 'resizing' || u.status === 'presigning' || u.status === 'uploading',
  );

  return {
    uploads,
    addImage,
    removeUpload,
    clearCompleted,
    clearAll,
    completedAttachmentIds,
    isUploading,
  };
}

function uploadToR2(
  url: string,
  blob: Blob,
  contentType: string,
  onProgress: (fraction: number) => void,
): Promise<void> {
  return new Promise((resolve, reject) => {
    const xhr = new XMLHttpRequest();
    xhr.open('PUT', url);
    xhr.setRequestHeader('Content-Type', contentType);

    xhr.upload.onprogress = (e) => {
      if (e.lengthComputable && e.total > 0) {
        onProgress(e.loaded / e.total);
      }
    };

    xhr.onload = () => {
      if (xhr.status >= 200 && xhr.status < 300) {
        resolve();
      } else {
        reject(new Error(`R2 upload failed: ${xhr.status} ${xhr.statusText}`));
      }
    };
    xhr.onerror = () => reject(new Error('Upload network error'));
    xhr.onabort = () => reject(new Error('Upload aborted'));
    xhr.send(blob);
  });
}
